import React, { useState } from 'react';
import {
  Box,
  CssBaseline,
  Toolbar,
  IconButton,
  AppBar as MuiAppBar,
  useTheme,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import MenuIcon from '@mui/icons-material/Menu';
import MoreIcon from '@mui/icons-material/MoreVert';
import AccountCircle from '@mui/icons-material/AccountCircle';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logo from '../../assets/img/conneqt_logo.png';
import lightLogo from '../../assets/img/conneQt_logo_light.png';
import { AppBarNotification } from './appBarNotification';
import { AppBarDesktopMenu } from './appBarDesktopMenu';
import { AppBarMobileMenu } from './appBarMobileMenu';
import { AppBarNavigationDrawerMobile } from './appBarNavigationDrawerMobile';
import { AppBarNavigationDrawerDesktop } from './appBarNavigationDrawerDesktop';
import { SyncStatusIndicator } from '../syncStatusIndicator/syncStatusIndicator';
import { LoggedInUser } from '../../state/helperServices/auth/auth.interfaces';
import { AccountTypes } from '../changeUser/accountTypes.enum';
import { ToggleThemeSwitch } from '../toggleThemeSwitch/toggleThemeSwitch';

interface AppBarProps {
  open?: boolean;
  handleDrawerMobileToggle: () => void;
  handleDrawerDesktopToggle: () => void;
  menuItems?: any;
  drawerNavigationItems: any;
  mobileOpen: boolean;
  unreadNotificationCount?: number;
  loggedInUser?: LoggedInUser;
}

export function AppBar({
  open,
  handleDrawerDesktopToggle,
  handleDrawerMobileToggle,
  mobileOpen,
  unreadNotificationCount,
  menuItems,
  drawerNavigationItems,
  loggedInUser,
}: AppBarProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] =
    useState<null | HTMLElement>(null);
  const navigator = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const matches = useMediaQuery('(max-width:540px)');

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const handleNotification = () => {
    navigator('./notifications');
  };

  const toggleMenu = () => {
    if (!open || open) {
      handleDrawerDesktopToggle();
    }
    if (!mobileOpen || mobileOpen) {
      handleDrawerMobileToggle();
    }
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <MuiAppBar
        position="fixed"
        sx={{
          bgcolor: theme.palette.background.default,
          zIndex: theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label={t('accessibility.OPEN_DRAWER')}
            onClick={toggleMenu}
            edge="start"
            sx={{ mr: 1 }}
          >
            <MenuIcon sx={{ fill: theme.palette.text.primary }} />
          </IconButton>
          <Box sx={{ flexGrow: 1 }} />
          {theme.palette.mode === 'light' ? (
            <img src={logo} alt="ConneQt" width={100} />
          ) : (
            <img src={lightLogo} alt="ConneQt" width={100} />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            <SyncStatusIndicator />
            <ToggleThemeSwitch />
            {loggedInUser?.accountType !== undefined &&
              loggedInUser?.accountType !== AccountTypes.Clinic && (
                <AppBarNotification
                  onNotificationClick={handleNotification}
                  unreadNotificationCount={unreadNotificationCount}
                />
              )}
            <IconButton
              data-testid="topBar-dropdown"
              size="large"
              edge="end"
              aria-label={t('accessibility.ACCOUNT_OF_CURRENT_USER')}
              aria-controls="account-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <AccountCircle sx={{ fill: theme.palette.text.primary }} />
            </IconButton>
          </Box>
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label={t('accessibility.SHOW_MORE')}
              aria-controls="account-menu-mobile"
              aria-haspopup="true"
              onClick={handleMobileMenuOpen}
              color="inherit"
            >
              <MoreIcon sx={{ fill: theme.palette.text.primary }} />
            </IconButton>
          </Box>
        </Toolbar>
      </MuiAppBar>
      <AppBarDesktopMenu
        menuItems={menuItems}
        menuId="account-menu"
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
      />
      <AppBarMobileMenu
        menuItems={menuItems}
        menuId="account-menu-mobile"
        anchorEl={mobileMoreAnchorEl}
        setAnchorEl={setMobileMoreAnchorEl}
        unreadNotificationCount={unreadNotificationCount}
        onNotificationClick={handleNotification}
        loggedInUser={loggedInUser}
      />
      {matches ? (
        <AppBarNavigationDrawerMobile
          drawerNavigationItems={drawerNavigationItems}
          mobileOpen={mobileOpen}
          handleDrawerMobileToggle={handleDrawerMobileToggle}
        />
      ) : (
        <AppBarNavigationDrawerDesktop
          drawerNavigationItems={drawerNavigationItems}
          open={open}
          handleDrawerDesktopToggle={handleDrawerDesktopToggle}
        />
      )}
    </Box>
  );
}
